/**-----------------------------------------------------------------------------------------
* Copyright © 2020 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import { Directive, TemplateRef, Optional, Component, Input, ElementRef, ContentChild, NgModule } from '@angular/core';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';

import * as ɵngcc0 from '@angular/core';

const _c0 = ["*"];
class PDFTemplateDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
}
PDFTemplateDirective.ɵfac = function PDFTemplateDirective_Factory(t) { return new (t || PDFTemplateDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.TemplateRef, 8)); };
PDFTemplateDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: PDFTemplateDirective, selectors: [["", "kendoPDFTemplate", ""]] });
/** @nocollapse */
PDFTemplateDirective.ctorParameters = () => [
    { type: TemplateRef, decorators: [{ type: Optional }] }
];
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFTemplateDirective, [{
        type: Directive,
        args: [{
                selector: '[kendoPDFTemplate]'
            }]
    }], function () { return [{ type: ɵngcc0.TemplateRef, decorators: [{
                type: Optional
            }] }]; }, null); })();

const FIELDS = ['bottom', 'left', 'right', 'top'];
/**
 * Represents the Kendo UI PDFMargin component for Angular.
 */
class PDFMarginComponent {
    /**
     * @hidden
     */
    get options() {
        const options = {};
        for (let idx = 0; idx < FIELDS.length; idx++) {
            const field = FIELDS[idx];
            const value = this[field];
            if (typeof value !== 'undefined') {
                options[field] = value;
            }
        }
        return options;
    }
}
PDFMarginComponent.ɵfac = function PDFMarginComponent_Factory(t) { return new (t || PDFMarginComponent)(); };
PDFMarginComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFMarginComponent, selectors: [["kendo-pdf-export-margin"]], inputs: { left: "left", top: "top", right: "right", bottom: "bottom" }, decls: 0, vars: 0, template: function PDFMarginComponent_Template(rf, ctx) { }, encapsulation: 2 });
PDFMarginComponent.propDecorators = {
    left: [{ type: Input }],
    top: [{ type: Input }],
    right: [{ type: Input }],
    bottom: [{ type: Input }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFMarginComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export-margin',
                template: ``
            }]
    }], null, { left: [{
            type: Input
        }], top: [{
            type: Input
        }], right: [{
            type: Input
        }], bottom: [{
            type: Input
        }] }); })();

/**
 * @hidden
 */
const compileTemplate = (templateRef) => {
    const context = {};
    let embeddedView = templateRef.createEmbeddedView(context);
    const result = (data) => {
        Object.assign(context, data);
        embeddedView.detectChanges();
        const templateWrap = document.createElement('span');
        embeddedView.rootNodes.forEach((rootNode) => {
            templateWrap.appendChild(rootNode.cloneNode(true));
        });
        return templateWrap;
    };
    result.destroy = () => {
        embeddedView.destroy();
        embeddedView = null;
    };
    return result;
};

/**
 * Represents the [Kendo UI PDF Export component for Angular]({% slug overview_pdfexport %}).
 *
 * @example
 * ```ts-preview
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <div class="example-config">
 *       <button class="k-button" (click)="pdf.saveAs('document.pdf')">
 *         Save As PDF...
 *       </button>
 *     </div>
 *
 *     <kendo-pdf-export #pdf paperSize="A4" margin="2cm">
 *       Content goes here
 *     </kendo-pdf-export>
 *   `
 * })
 * export class AppComponent {
 * }
 * ```
 */
class PDFExportComponent {
    constructor(element) {
        this.element = element;
        /**
         * The creator of the PDF document.
         * @default "Kendo UI PDF Generator"
         */
        this.creator = 'Kendo UI PDF Generator';
        /**
         * Specifies the name of the exported PDF file.
         * @default "Export.pdf"
         */
        this.fileName = 'export.pdf';
    }
    get drawMargin() {
        const marginComponent = this.marginComponent;
        let margin = this.margin;
        if (marginComponent) {
            margin = Object.assign(margin || {}, marginComponent.options);
        }
        return margin;
    }
    /**
     * Saves the content as a PDF file with the specified name.
     * @param fileName - The name of the exported file.
     */
    saveAs(fileName = this.fileName) {
        this.save(this.element.nativeElement, fileName);
    }
    /**
     * Exports the content as a `Group` for further processing.
     *
     * @return - The root group of the exported scene.
     */
    export() {
        return this.exportElement(this.element.nativeElement);
    }
    save(element, fileName) {
        this.exportElement(element)
            .then(group => this.exportGroup(group, this.pdfOptions()))
            .then(dataUri => this.saveDataUri(dataUri, fileName, this.saveOptions()));
    }
    exportElement(element) {
        const promise = this.drawElement(element, this.drawOptions());
        const cleanup = this.cleanup.bind(this);
        promise.then(cleanup, cleanup);
        return promise;
    }
    cleanup() {
        if (this.pageTemplate) {
            this.pageTemplate.destroy();
            delete this.pageTemplate;
        }
    }
    drawOptions() {
        if (this.pageTemplateDirective) {
            this.pageTemplate = compileTemplate(this.pageTemplateDirective.templateRef);
        }
        return {
            avoidLinks: this.avoidLinks,
            forcePageBreak: this.forcePageBreak,
            keepTogether: this.keepTogether,
            margin: this.drawMargin,
            paperSize: this.paperSize,
            landscape: this.landscape,
            repeatHeaders: this.repeatHeaders,
            scale: this.scale,
            template: this.pageTemplate
        };
    }
    pdfOptions() {
        return {
            autoPrint: this.autoPrint,
            author: this.author,
            creator: this.creator,
            date: this.date,
            imgDPI: this.imageResolution,
            keywords: this.keywords,
            landscape: this.landscape,
            margin: this.drawMargin,
            multiPage: true,
            paperSize: this.paperSize,
            producer: this.producer,
            subject: this.subject,
            title: this.title
        };
    }
    saveOptions() {
        return {
            forceProxy: this.forceProxy,
            proxyData: this.proxyData,
            proxyTarget: this.proxyTarget,
            proxyURL: this.proxyURL
        };
    }
    drawElement(element, options) {
        return drawDOM(element, options);
    }
    exportGroup(group, options) {
        return exportPDF(group, options);
    }
    saveDataUri(dataUri, fileName, options) {
        saveAs(dataUri, fileName, options);
    }
}
PDFExportComponent.ɵfac = function PDFExportComponent_Factory(t) { return new (t || PDFExportComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
PDFExportComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFExportComponent, selectors: [["kendo-pdf-export"]], contentQueries: function PDFExportComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFTemplateDirective, true);
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFMarginComponent, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.pageTemplateDirective = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.marginComponent = _t.first);
    } }, inputs: { creator: "creator", fileName: "fileName", autoPrint: "autoPrint", author: "author", avoidLinks: "avoidLinks", forcePageBreak: "forcePageBreak", keepTogether: "keepTogether", date: "date", imageResolution: "imageResolution", forceProxy: "forceProxy", keywords: "keywords", landscape: "landscape", margin: "margin", paperSize: "paperSize", repeatHeaders: "repeatHeaders", scale: "scale", proxyData: "proxyData", proxyURL: "proxyURL", proxyTarget: "proxyTarget", producer: "producer", subject: "subject", title: "title" }, ngContentSelectors: _c0, decls: 2, vars: 0, template: function PDFExportComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵprojectionDef();
        ɵngcc0.ɵɵelementStart(0, "div");
        ɵngcc0.ɵɵprojection(1);
        ɵngcc0.ɵɵelementEnd();
    } }, encapsulation: 2 });
/** @nocollapse */
PDFExportComponent.ctorParameters = () => [
    { type: ElementRef }
];
PDFExportComponent.propDecorators = {
    autoPrint: [{ type: Input }],
    author: [{ type: Input }],
    avoidLinks: [{ type: Input }],
    forcePageBreak: [{ type: Input }],
    keepTogether: [{ type: Input }],
    creator: [{ type: Input }],
    date: [{ type: Input }],
    imageResolution: [{ type: Input }],
    fileName: [{ type: Input }],
    forceProxy: [{ type: Input }],
    keywords: [{ type: Input }],
    landscape: [{ type: Input }],
    margin: [{ type: Input }],
    paperSize: [{ type: Input }],
    repeatHeaders: [{ type: Input }],
    scale: [{ type: Input }],
    proxyData: [{ type: Input }],
    proxyURL: [{ type: Input }],
    proxyTarget: [{ type: Input }],
    producer: [{ type: Input }],
    subject: [{ type: Input }],
    title: [{ type: Input }],
    pageTemplateDirective: [{ type: ContentChild, args: [PDFTemplateDirective,] }],
    marginComponent: [{ type: ContentChild, args: [PDFMarginComponent,] }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export',
                template: `<div><ng-content></ng-content></div>`
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }]; }, { creator: [{
            type: Input
        }], fileName: [{
            type: Input
        }], autoPrint: [{
            type: Input
        }], author: [{
            type: Input
        }], avoidLinks: [{
            type: Input
        }], forcePageBreak: [{
            type: Input
        }], keepTogether: [{
            type: Input
        }], date: [{
            type: Input
        }], imageResolution: [{
            type: Input
        }], forceProxy: [{
            type: Input
        }], keywords: [{
            type: Input
        }], landscape: [{
            type: Input
        }], margin: [{
            type: Input
        }], paperSize: [{
            type: Input
        }], repeatHeaders: [{
            type: Input
        }], scale: [{
            type: Input
        }], proxyData: [{
            type: Input
        }], proxyURL: [{
            type: Input
        }], proxyTarget: [{
            type: Input
        }], producer: [{
            type: Input
        }], subject: [{
            type: Input
        }], title: [{
            type: Input
        }], pageTemplateDirective: [{
            type: ContentChild,
            args: [PDFTemplateDirective]
        }], marginComponent: [{
            type: ContentChild,
            args: [PDFMarginComponent]
        }] }); })();

const COMPONENT_DIRECTIVES = [
    PDFExportComponent,
    PDFMarginComponent,
    PDFTemplateDirective
];
/**
 * Represents the [NgModule]({{ site.data.urls.angular['ngmoduleapi'] }}) definition for the PDF Export directive.
 */
class PDFExportModule {
}
PDFExportModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: PDFExportModule });
PDFExportModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function PDFExportModule_Factory(t) { return new (t || PDFExportModule)(); } });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(PDFExportModule, { declarations: [PDFExportComponent,
        PDFMarginComponent,
        PDFTemplateDirective], exports: [PDFExportComponent,
        PDFMarginComponent,
        PDFTemplateDirective] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportModule, [{
        type: NgModule,
        args: [{
                declarations: [COMPONENT_DIRECTIVES],
                exports: [COMPONENT_DIRECTIVES]
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { PDFExportComponent, PDFExportModule, PDFMarginComponent, PDFTemplateDirective, compileTemplate };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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